/**
 * checkDeviceRequirements.js
 * --------------------------
 * Runtime gatekeeper that decides whether the client device is allowed to run
 * the camera workflow.
 *
 * Returns:
 *   true  -> device passes OS and camera checks
 *   false -> any requirement failed
 */
import { firebaseApp } from "./firebaseApp";
import { collection, getDocs, getFirestore } from "firebase/firestore";

function extractMajorVersion(userAgent, osToken) {
  const versionFragment = userAgent
    .substring(userAgent.indexOf(osToken) + osToken.length)
    .match(/\d+/);

  if (!versionFragment) return null;

  const parsed = parseInt(versionFragment[0], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function capabilityMax(value) {
  if (typeof value === "number") return value;
  if (value && typeof value.max === "number") return value.max;
  return null;
}

function meetsCameraRequirements(capabilities) {
  const heightMax = capabilityMax(capabilities?.height);
  const widthMax = capabilityMax(capabilities?.width);
  const frameRateMax = capabilityMax(capabilities?.frameRate);

  const has720p =
    (heightMax != null && heightMax >= 720) ||
    (widthMax != null && widthMax >= 1280);
  const has30Fps = frameRateMax != null && frameRateMax >= 30;

  return has720p && has30Fps;
}

export const checkDeviceRequirements = async (userAgent) => {
  try {
    const db = getFirestore(firebaseApp);
    const collectionRef = collection(db, "device_requirements");
    const querySnapshot = await getDocs(collectionRef);

    let meetsOsRequirement = false;

    querySnapshot.forEach((snapshotDoc) => {
      if (!userAgent.includes(snapshotDoc.id)) return;

      const minimumOS = snapshotDoc.data()?.minimum_os;
      const osVersion = extractMajorVersion(userAgent, snapshotDoc.id);

      if (Number.isFinite(minimumOS) && osVersion != null && osVersion >= minimumOS) {
        meetsOsRequirement = true;
      }
    });

    if (!meetsOsRequirement) return false;

    const stream = await navigator.mediaDevices.getUserMedia({ video: true });

    try {
      const videoTrack = stream.getVideoTracks?.()[0];
      const capabilities = videoTrack?.getCapabilities?.();

      if (!capabilities) return false;
      return meetsCameraRequirements(capabilities);
    } finally {
      const videoTrack = stream.getVideoTracks?.()[0];
      videoTrack?.stop?.();
    }
  } catch (error) {
    console.error("Error checking device requirements:", error);
    return false;
  }
};
